import { degreesToDms } from "./geo-service";
import moment from "moment-timezone";
import numeral from "numeral";
import { findEntityRecursively } from "../utils/entities/findEntityRecursively";
import { isValueNullish } from "../utils";
import { toTwelveHourParts } from "../utils/timeValues";
import { formatSize } from "../utils/files.js";

function fieldValueHelper($logger) {
	const formatters = {
		Location: (field, fieldValue) => {
			const dms = degreesToDms(fieldValue[0].Number);

			return `${dms.degrees}° ${dms.minutes}'`;
		},
		Coordinate: (field, fieldValue) => {
			if(fieldValue[0].Latitude !== undefined && fieldValue[0].Longitude !== undefined) {
				return `${fieldValue[0].Latitude}, ${fieldValue[0].Longitude}`;
			}
			return "--";
		},
		Tags: (field, fieldValue) => {
			return fieldValue.map(val => {
				if(val.EntityId !== undefined && val.EntityId !== null) {
					return getEntityItemName(field, val);
				} else if(val.DataId !== undefined && val.DataId !== null) {
					return getDataItemName(field, val);
				} else if(val.String !== undefined && val.String !== null) {
					return val.String;
				}
				return "--";
			}).join(", ");
		},
		Date: (field, fieldValue) => {
			if(fieldValue[0].Date !== undefined) {
				const localShiftedMoment = moment(fieldValue[0].Date, "YYYY-MM-DDThh:mm:ss");

				return `${moment(localShiftedMoment).format("MM-DD-YYYY")}`;
			}
			return "--";
		},
		DateTime: (field, fieldValue) => {
			if(fieldValue[0].Date !== undefined) {
				const localShiftedMoment = moment(fieldValue[0].Date, "YYYY-MM-DDThh:mm:ss");

				return `${moment(localShiftedMoment).format("MM-DD-YYYY h:mm A")}`;
			}
			return "--";
		},
		Time: (field, fieldValue) => {
			const timeParts = toTwelveHourParts(fieldValue.Time);

			if(timeParts !== undefined) {
				return `${timeParts.hour}:${timeParts.minute} ${timeParts.period}`;
			}

			return "--";
		},
		Number: (field, fieldValue) => {
			if(fieldValue[0].Number !== undefined) {
				let formatString = "0,0";
				if(field.RoundingPoint > 0) {
					formatString = formatString + "." + "0".repeat(field.RoundingPoint);
				}

				return `${numeral(fieldValue[0].Number).format(formatString)}`;
			}
			return "--";
		},
		Boolean: (field, fieldValue) => {
			if(fieldValue[0].Boolean !== undefined) {
				return `${fieldValue[0].Boolean ? (field.YesLabel ?? "Yes") : (field.NoLabel ?? "No")}`;
			}
			return "--";
		},
		SingleFile: (field, fieldValue) => formatFileValue(fieldValue),
		ImageFile: (field, fieldValue) => formatFileValue(fieldValue),
		PreSelect: (field, fieldValue) => getDataItemName(field, fieldValue[0]),
		Text: (field, fieldValue) => {
			return fieldValue[0].String ? fieldValue[0].String : "--";
		},
		StringMulti: (field, fieldValue) => {
			const values = [...fieldValue]
				.sort((leftValue, rightValue) => (leftValue.Ordinal ?? 0) - (rightValue.Ordinal ?? 0))
				.map(value => value.String)
				.filter(value => value !== undefined && value !== null && value.trim() !== "")
				.map(value => value.trim());

			return values.length > 0 ? values.join(", ") : "--";
		},
		Selection: (field, fieldValue) => getDataItemName(field, fieldValue[0]),
		EntitySelector: (field, fieldValue) => getEntityItemName(field, fieldValue[0])
	};

	this.formatFieldValue = (field, fieldValue) => {
		if(isValueNullish(fieldValue) || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
			return "--";
		}

		if(!Array.isArray(fieldValue)) {
			fieldValue = [fieldValue];
		}

		const formatter = formatters[field.Template];
		if(formatter) {
			return formatter(field, fieldValue);
		}
		return "--";
	};

	this.getNumberUnit = (field, fieldValue) => {
		if(isValueNullish(fieldValue) || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
			return "";
		}

		if(!Array.isArray(fieldValue)) {
			fieldValue = [fieldValue];
		}

		if(field.Template !== "Number" && field.Template !== "Location") {
			return "";
		}

		const unitDataItem = field.Data?.find(d => d.Id === (fieldValue[0].DataId ?? fieldValue[0].EntityId));

		return unitDataItem?.Abbreviation ?? unitDataItem.Code ?? "";
	};

	function formatFileValue(fieldValue) {
		const file = fieldValue[0]?.File;
		const fileName = file?.Name ?? file?.name;
		const fileLength = file?.Length ?? file?.length;

		if(!fileName) {
			return "--";
		}

		return fileLength === undefined || fileLength === null ? fileName : `${fileName} (${formatSize(fileLength)})`;
	}

	function getDataItemName(field, fieldValue) {
		if(field.IsCustomEntryEnabled === true && fieldValue.String !== undefined) {
			return fieldValue.String;
		}

		if(fieldValue.IsNotAvailable === true) {
			return "Not Available";
		}

		if(field.Type.IsEntity) {
			return getEntityItemName(field, fieldValue);
		}

		const item = field.Data.find(d => d.Id === fieldValue.DataId);

		const value = item?.Name ?? item?.Code;

		return value || "--";
	}

	function getEntityItemName(field, fieldValue) {
		if(field.IsCustomEntryEnabled === true && fieldValue.String !== undefined) {
			return fieldValue.String;
		}
		const item = findEntityRecursively(field.Data, fieldValue.EntityId);
		if(item.match) {
			if(item.depth > 0) {
				return `${item[0].Name ?? item[0].Code} (${item.match.Name ?? item.match.Code})`;
			}

			return item.match.Name ?? item.match.Code;
		}

		return "--";
	}
}

export default angular
	.module("vesl.services.field-value-helper", [])
	.service("fieldValueHelper", fieldValueHelper).name;
